import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { ArrowLeft, Save, User, FileText, Calendar, MapPin, Phone, Mail, Upload, Eye, Copy, X } from "lucide-react";
import "../styles/Admin.css";
import { getApplicationById, updateApplicationStatus, requestReupload } from "../ServiceAPI/AdminUser/applicationapi";
import { BACKEND_BASE_URL } from "../config/api";

const getUploadUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;

  const normalizedValue = value.replace(/\\/g, '/').trim();
  let relativePath = normalizedValue;

  if (relativePath.includes('/uploads/')) {
    relativePath = relativePath.substring(relativePath.indexOf('/uploads/') + 1);
  } else if (relativePath.startsWith('/')) {
    relativePath = relativePath.slice(1);
  }

  return `${BACKEND_BASE_URL.replace(/\/+$/, '')}/${relativePath}`;
};

const ApplicationDetails = () => {
  const navigate = useNavigate();
  const { applicationId } = useParams();
  const [application, setApplication] = useState(null);
  const [formData, setFormData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState("submitted");
  const [remarks, setRemarks] = useState("");
  const [applicationFee, setApplicationFee] = useState("");
  const [isPaymentLinkModalOpen, setIsPaymentLinkModalOpen] = useState(false);
  const [paymentLink, setPaymentLink] = useState("");
  const [copyMessage, setCopyMessage] = useState("");

  // Fetch application details
  useEffect(() => {
    async function fetchApplicationDetails() {
      try {
        setLoading(true);
        const data = await getApplicationById(applicationId);
        console.log("Application details:", data);

        if (data && data.application) {
          setApplication(data.application);
          setNewStatus(data.application.Status || "submitted");
          setRemarks(data.application.Remarks || "");
          setApplicationFee(
            data.application.ApplicationFee !== null && data.application.ApplicationFee !== undefined
              ? String(data.application.ApplicationFee)
              : ""
          );
          if (data.formData) {
            setFormData(data.formData);
          }
        }
      } catch (err) {
        console.error("Error fetching application details:", err);
        setError("Failed to load application details");
      } finally {
        setLoading(false);
      }
    }

    if (applicationId) {
      fetchApplicationDetails();
    }
  }, [applicationId]);

  // Check authentication
  useEffect(() => {
    const isAdminAuthenticated = localStorage.getItem("adminAuth") === "true";
    if (!isAdminAuthenticated) {
      navigate("/admin/login");
    }
  }, [navigate]);

  const handleStatusUpdate = async () => {
    try {
      setIsUpdating(true);
      await updateApplicationStatus(applicationId, {
        status: newStatus,
        remarks: remarks,
        applicationFee
      });

      // Update local state
      if (application) {
        setApplication({
          ...application,
          Status: newStatus,
          Remarks: remarks,
          ApplicationFee: applicationFee === "" ? null : Number(applicationFee)
        });
      }

      alert("Application status updated successfully!");
      // Navigate back with refresh trigger
      setTimeout(() => {
        navigate("/admin/applications", { replace: false });
      }, 500);
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update application status");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReupload = async (dataId, currentRequested) => {
    try {
      const newRequested = !currentRequested;
      await requestReupload(applicationId, dataId, newRequested);

      // Update local state
      setFormData(prevData =>
        prevData.map(field =>
          field.DataId === dataId
            ? { ...field, ReuploadRequested: newRequested ? 1 : 0 }
            : field
        )
      );

      alert(`Reupload ${newRequested ? 'requested' : 'cancelled'} successfully!`);
    } catch (err) {
      console.error("Error toggling reupload:", err);
      alert("Failed to update reupload request");
    }
  };

  const generatePaymentLink = () => {
    if (!application) return;

    const applicationNo = application.ApplicationNumber || application.ApplicationNo || application.applicationNumber || "";
    const createdAt = application.ApplicationDate || application.CreatedAt || application.Created_at || application.createdAt || "";

    if (!applicationNo || !createdAt) {
      setCopyMessage("Unable to generate payment link for this application.");
      setPaymentLink("");
      setIsPaymentLinkModalOpen(true);
      return;
    }

    const payload = { applicationNo, createdAt };
    const token = btoa(JSON.stringify(payload));
    const siteBase = window?.location?.origin || "https://www.rtobharat.com";
    const link = `${siteBase.replace(/\/+$/, "")}/public-payment/${token}`;
    setPaymentLink(link);
    setCopyMessage("");
    setIsPaymentLinkModalOpen(true);
  };

  const copyPaymentLink = async () => {
    if (!paymentLink) return;

    try {
      await navigator.clipboard.writeText(paymentLink);
      setCopyMessage("Payment link copied successfully.");
    } catch (err) {
      console.error("Failed to copy payment link:", err);
      setCopyMessage("Failed to copy payment link.");
    }
  };

  const handleBack = () => {
    navigate("/admin/applications");
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading application details...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error || !application) {
    return (
      <AdminLayout>
        <div className="admin-page-header">
          <h1 className="admin-page-title">Application Details</h1>
        </div>
        <div className="alert alert-error">
          {error || "Application not found"}
        </div>
        <button className="btn-secondary-modern" onClick={handleBack}>
          <ArrowLeft size={18} />
          Back to Applications
        </button>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <button className="btn-back" onClick={handleBack}>
            <ArrowLeft size={18} />
            Back to Applications
          </button>
          <h1 className="admin-page-title">Application Details</h1>
          <p className="admin-page-subtitle">Review and manage application status</p>
        </div>
        <button className="btn-primary-modern" onClick={generatePaymentLink}>
          <Copy size={18} />
          Generate Payment Link
        </button>
      </div>

      <div className="admin-content-area">
        <div className="details-layout">
          {/* User Information Card */}
          <div className="details-card">
            <div className="details-card-header">
              <User size={24} />
              <h2>User Information</h2>
            </div>
            <div className="details-grid-modern">
              <div className="detail-item-modern">
                <label><User size={16} /> Username</label>
                <p>{application.Username || "N/A"}</p>
              </div>
              <div className="detail-item-modern">
                <label><Mail size={16} /> Email</label>
                <p>{application.Email || "N/A"}</p>
              </div>
              <div className="detail-item-modern">
                <label><Phone size={16} /> Phone</label>
                <p>{application.Phone || "N/A"}</p>
              </div>
              <div className="detail-item-modern">
                <label><Calendar size={16} /> Date of Birth</label>
                <p>
                  {application.DOB
                    ? new Date(application.DOB).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
              <div className="detail-item-modern">
                <label><MapPin size={16} /> State</label>
                <p>{application.StateName || "N/A"}</p>
              </div>
              <div className="detail-item-modern">
                <label>Verification Status</label>
                <p>
                  <span className={`status-badge-modern ${application.isEmailVerified === 1 && application.isPhoneVerified === 1
                    ? 'approved'
                    : 'unverified'
                    }`}>
                    {application.isEmailVerified === 1 && application.isPhoneVerified === 1
                      ? 'Verified'
                      : 'Unverified'}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Service Information Card */}
          <div className="details-card">
            <div className="details-card-header">
              <FileText size={24} />
              <h2>Service Information</h2>
            </div>
            <div className="details-grid-modern">
              <div className="detail-item-modern">
                <label>Service Name</label>
                <p>{application.ServiceName || "N/A"}</p>
              </div>
              <div className="detail-item-modern">
                <label>Service Type</label>
                <p>{application.ServiceType || "N/A"}</p>
              </div>
              <div className="detail-item-modern">
                <label>Service Category</label>
                <p>{application.ServiceCategory || "N/A"}</p>
              </div>
              <div className="detail-item-modern">
                <label>Application Date</label>
                <p>
                  {application.ApplicationDate
                    ? new Date(application.ApplicationDate).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Form Data Table */}
          {formData && formData.length > 0 && (
            <div className="details-card full-width">
              <div className="details-card-header">
                <FileText size={24} />
                <h2>Application Form Data</h2>
              </div>
              <div className="table-responsive">
                <table className="modern-table compact">
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>Field Label</th>
                      <th>Field Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.map((field, index) => (
                      <tr key={field.DataId || index} className={field.ReuploadRequested ? "row-reupload-requested" : ""}>
                        <td>
                          <strong>{field.FieldLabel || "N/A"}</strong>
                          {field.ReuploadRequested === 1 && (
                            <span className="badge-reupload-needed">Reupload Requested</span>
                          )}
                        </td>
                        <td>
                          {field.InputType === 'file' ? (
                            <div className="admin-file-action-container">
                              {field.Value ? (
                                <a
                                  href={getUploadUrl(field.Value)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="view-doc-link"
                                >
                                  <Eye size={16} />
                                  View Document
                                </a>
                              ) : (
                                <span className="no-file-text">No file uploaded</span>
                              )}
                              <button
                                className={`btn-reupload-mini ${field.ReuploadRequested ? 'active' : ''}`}
                                onClick={() => handleReupload(field.DataId, field.ReuploadRequested === 1)}
                                title={field.ReuploadRequested ? "Cancel Reupload Request" : "Request Reupload"}
                              >
                                <Upload size={14} />
                                {field.ReuploadRequested ? "Requested" : "Reupload"}
                              </button>
                            </div>
                          ) : (
                            field.Value || "N/A"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Status Update Card */}
          <div className="details-card full-width">
            <div className="details-card-header">
              <Save size={24} />
              <h2>Status & Remarks</h2>
            </div>
            <div className="status-update-form-modern">
              <div className="form-row">
                <div className="form-group-modern">
                  <label>Application Status</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="status-select-modern"
                  >
                    <option value="SUBMITTED">Submitted</option>
                    <option value="REUPLOAD_REQUESTED">Reupload Requested</option>
                    <option value="DOCUMENTS_VERIFIED">Documents Verified</option>
                    <option value="PAYMENT_PENDING">Payment Pending</option>
                    <option value="PAYMENT_DIDNT_RECEIVE">Payment Didn't Receive</option>
                    <option value="PAYMENT_COMPLETED">Payment Completed</option>
                    <option value="IN_PROCESSING">In Processing</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>
                <div className="form-group-modern">
                  <label>Application Fee (INR)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={applicationFee}
                    onChange={(e) => setApplicationFee(e.target.value)}
                    className="status-select-modern"
                    placeholder="Set fee after document verification"
                  />
                </div>
              </div>

              <div className="form-group-modern">
                <label>Remarks</label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Enter remarks for this application..."
                  rows="4"
                  className="remarks-textarea-modern"
                />
              </div>

              <div className="form-actions-modern">
                <button
                  className="btn-secondary-modern"
                  onClick={handleBack}
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary-modern"
                  onClick={handleStatusUpdate}
                  disabled={isUpdating}
                >
                  <Save size={18} />
                  {isUpdating ? "Updating..." : "Update Status"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isPaymentLinkModalOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", zIndex: 1000 }}>
          <div style={{ width: "100%", maxWidth: "720px", background: "white", borderRadius: "16px", boxShadow: "0 20px 45px rgba(0,0,0,0.2)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem 1.25rem", borderBottom: "1px solid #e5e7eb" }}>
              <div>
                <h3 style={{ fontSize: "1.1rem", fontWeight: 700, margin: 0 }}>Public Payment Link</h3>
                <p style={{ margin: "0.25rem 0 0", fontSize: "0.95rem", color: "#6b7280" }}>Share this link with the applicant to open the public payment page.</p>
              </div>
              <button
                onClick={() => {
                  setIsPaymentLinkModalOpen(false);
                  setCopyMessage("");
                }}
                style={{ border: "none", background: "transparent", cursor: "pointer", padding: "0.4rem", borderRadius: "999px" }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: "1.25rem" }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: "0.5rem" }}>Payment URL</label>
              <textarea
                readOnly
                value={paymentLink || "Unable to generate link for this application."}
                rows={5}
                style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "12px", padding: "0.85rem", fontFamily: "monospace", fontSize: "0.95rem", resize: "none", background: "#f9fafb" }}
              />

              {copyMessage && (
                <div style={{ marginTop: "0.75rem", padding: "0.75rem 0.9rem", borderRadius: "10px", background: copyMessage.includes("success") ? "#ecfdf3" : "#fef2f2", color: copyMessage.includes("success") ? "#047857" : "#b91c1c", fontSize: "0.95rem" }}>
                  {copyMessage}
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1rem" }}>
                <button className="btn-secondary-modern" onClick={copyPaymentLink}>
                  <Copy size={16} />
                  Copy Link
                </button>
                <button
                  className="btn-secondary-modern"
                  onClick={() => {
                    setIsPaymentLinkModalOpen(false);
                    setCopyMessage("");
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ApplicationDetails;
